import { setTerminal } from '@/common/apis/services/auth/setTerminal';
import removeSubdomain from '@/common/utils/removeSubdomain';
import { dayToSecond } from '@/common/utils/dayToSecond';

export const FIRST_TOUCH_COOKIE = 'p24_attr_first';
export const FIRST_TOUCH_MAX_AGE_SEC = dayToSecond(90);

export type FirstTouchAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer: string;
  landing_page: string;
  landing_page_full: string;
  landing_timestamp: string;
  terminal_id?: string;
};

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

function writeFirstTouchCookie(value: FirstTouchAttribution): void {
  if (typeof document === 'undefined') return;
  const domain = removeSubdomain(window.location.hostname);
  document.cookie = `${FIRST_TOUCH_COOKIE}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${FIRST_TOUCH_MAX_AGE_SEC}; domain=${domain}; SameSite=Lax`;
}

/** First-touch only — does not overwrite an existing snapshot. */
export async function captureFirstTouchAttribution(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (readCookie(FIRST_TOUCH_COOKIE)) return;

  const terminalId = (await setTerminal()) as string | undefined;
  const snapshot: FirstTouchAttribution = {
    referrer: document.referrer || '',
    landing_page: window.location.pathname,
    landing_page_full: window.location.href,
    landing_timestamp: new Date().toISOString(),
  };

  const params = new URLSearchParams(window.location.search);
  for (const key of UTM_PARAM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) snapshot[key] = value;
  }

  if (terminalId) snapshot.terminal_id = String(terminalId);

  writeFirstTouchCookie(snapshot);
}
